import React, {useState} from 'react';

import {Button, Dialogue, Icon, Modal, Radio, Text, addToast} from '@albert-io/atomic';
import {normalizeError} from 'lib/normalizeError';

import {useReleaseGradesMutation} from './queries/releaseGrades.queries.generated';

type ReleaseScope = 'class' | 'all';

const classListFormatter = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'});

interface Counts {
  gradedCount: number;
  totalCount: number;
  /** Graded but not yet released; the cross-class value arrives with the scope lookup. */
  releasableCount?: number;
}

interface ReleaseGradesModalProps {
  assignmentId: string;
  assignmentName: string;
  /**
   * The class the teacher is viewing; scopes a "This class only" release. Absent when entering from
   * All Classwork, where the release is every class the teacher teaches and there is no choice.
   */
  classroomId?: string;
  classroomName?: string;
  /** FRQ-response counts for the scope above. */
  gradedCount: number;
  totalCount: number;
  /** Graded but not yet released to this class. */
  releasableCount: number;
  /**
   * Whether this assignment reaches classes beyond the one being viewed. Only then does the
   * "Release for" choice appear — with a single class there is nothing to choose.
   */
  spansMultipleClasses?: boolean;
  /** Names of the classes an "All classes" release would reach, alphabetically. */
  classroomNames?: string[];
  /** Counts across every class, for the "All classes" summary line. */
  allClassesCounts?: Counts;
  /** True until the release scope above is known; confirming early could release too narrowly. */
  isScopeLoading?: boolean;
  /** The scope lookup failed, so "All classes" can't be offered. */
  hasScopeError?: boolean;
  onClose: () => void;
}

export const ReleaseGradesModal = ({
  assignmentId,
  assignmentName,
  classroomId,
  classroomName,
  gradedCount,
  totalCount,
  releasableCount,
  spansMultipleClasses,
  classroomNames,
  allClassesCounts,
  isScopeLoading,
  hasScopeError,
  onClose
}: ReleaseGradesModalProps) => {
  const [isReleasing, setIsReleasing] = useState(false);
  const [scope, setScope] = useState<ReleaseScope>('class');
  const [releaseGrades] = useReleaseGradesMutation();

  // Entered from All Classwork: there is no single class to narrow to, so the release is
  // all-classes by definition and the choice is suppressed.
  const isAllClassesEntry = !classroomId;
  const canChooseScope = !isAllClassesEntry && Boolean(spansMultipleClasses);
  const isAllClasses = isAllClassesEntry || (canChooseScope && scope === 'all');

  const counts =
    isAllClasses && allClassesCounts
      ? allClassesCounts
      : {gradedCount, totalCount, releasableCount};
  // Neither count alone sees all finished grading: nothing publishes until the release sweep, and
  // releasableCount drops a guess once any of that student's classes is released. Summing assumes
  // they stay disjoint, which a dropped enrollment after a partial release can break — hence clamp.
  const gradedDisplayCount = Math.min(
    counts.totalCount,
    counts.gradedCount + (counts.releasableCount ?? 0)
  );
  const ungradedCount = Math.max(counts.totalCount - gradedDisplayCount, 0);
  const scopedReleasableCount = counts.releasableCount;

  const handleRelease = async () => {
    if (isReleasing) {
      return;
    }
    setIsReleasing(true);
    try {
      // Omitting classroomIds releases every class of the assignment the teacher teaches.
      const {data} = await releaseGrades({
        variables: {
          input:
            isAllClasses || !classroomId
              ? {assignmentId}
              : {assignmentId, classroomIds: [classroomId]}
        },
        // The mutation returns classroom assignments, so the normalized write covers
        // `gradesReleased` but nothing about the guesses the release sweep published. By name, so
        // this only costs a request where those reads are actually mounted (the submission view).
        refetchQueries: [
          'SubmissionGuessStatuses',
          'AssignmentGradingStatuses',
          'GuessesForGrading'
        ]
      });
      // The action silently skips classes the actor doesn't teach, so an empty list means
      // nothing was released — reporting success there would hide grades the teacher thinks
      // they published.
      if (!data?.releaseGrades.length) {
        addToast({
          color: 'negative',
          title: "Couldn't release grades",
          message: 'You can only release grades for classes you teach.'
        });
        setIsReleasing(false);
        return;
      }
      addToast({
        color: 'positive',
        title: 'Success!',
        message: `Released grades for ${assignmentName}.`
      });
      onClose();
    } catch (error) {
      addToast({
        color: 'negative',
        title: "Couldn't release grades",
        message: normalizeError(error).message
      });
      setIsReleasing(false);
    }
  };

  return (
    <Modal
      ariaLabel={`Release grades for ${assignmentName}`}
      handleClose={onClose}
      dismissable={!isReleasing}
    >
      {({modalContentStyle}) => (
        <Dialogue
          inModal
          size='m'
          alignTitle='left'
          title={`Release Grades for ${assignmentName}?`}
          handleClose={isReleasing ? undefined : onClose}
          className={modalContentStyle}
        >
          <Dialogue.Body>
            <Text as='p' bold>
              {`${gradedDisplayCount} of ${counts.totalCount} responses graded ${
                isAllClasses ? 'across all classes' : `in ${classroomName}`
              }`}
            </Text>
            {ungradedCount > 0 ? (
              <Text as='p' className='mt-x0-half'>
                {`${ungradedCount} ${
                  ungradedCount === 1 ? 'response' : 'responses'
                } not yet graded — these will stay hidden from students until you grade and release them.`}
              </Text>
            ) : null}
            {scopedReleasableCount !== undefined && gradedDisplayCount > 0 ? (
              <Text as='p' className='mt-x0-half'>
                {scopedReleasableCount > 0
                  ? `${scopedReleasableCount} graded ${
                      scopedReleasableCount === 1 ? 'response' : 'responses'
                    } will be released.`
                  : 'All graded responses have already been released.'}
              </Text>
            ) : null}

            {hasScopeError ? (
              <Text as='p' size='xs' color='warning' className='mt-x2'>
                {isAllClassesEntry
                  ? 'We couldn’t list your classes, but this will still release grades for every class you teach.'
                  : `We couldn’t check your other classes, so this will release for ${classroomName} only.`}
              </Text>
            ) : null}

            {canChooseScope ? (
              <fieldset className='mt-x3 p-x0 border-0'>
                <Text as='legend' bold size='s' className='mb-x1'>
                  Release for
                </Text>
                <div className='gap-x1 flex flex-col'>
                  <ScopeOption
                    checked={scope === 'class'}
                    onChange={() => setScope('class')}
                    disabled={isReleasing}
                    label='This class only'
                    description={`Release grades for students in ${classroomName}.`}
                    testId='release-scope-class'
                  />
                  <ScopeOption
                    checked={scope === 'all'}
                    onChange={() => setScope('all')}
                    disabled={isReleasing}
                    label='All classes'
                    description={
                      classroomNames?.length
                        ? `Release grades for students in ${classListFormatter.format(
                            classroomNames
                          )}.`
                        : 'Release grades for students in every class this is assigned to.'
                    }
                    testId='release-scope-all'
                  />
                </div>
              </fieldset>
            ) : null}

            <Text as='p' size='xs' color='secondary' className='mt-x3'>
              Releasing can’t be undone. Students see their score and feedback as soon as you
              release.
            </Text>
          </Dialogue.Body>
          <Dialogue.BtnGroup fillColumn>
            <Button
              variant='outlined'
              color='secondary'
              disabled={isReleasing}
              onClick={onClose}
              className='flex-1'
              data-testid='release-grades-cancel'
            >
              Cancel
            </Button>
            <Button
              color='primary'
              // Confirming before the scope resolves could release one class when the teacher
              // meant all of them.
              disabled={isReleasing || isScopeLoading}
              onClick={handleRelease}
              className='flex-1'
              data-testid='release-grades-confirm'
            >
              <span className='flex-1 text-center'>Release Grades</span>
              <Icon
                icon={isReleasing || isScopeLoading ? 'spinner' : 'paper-plane'}
                iconStyle='solid'
                spin={isReleasing || isScopeLoading}
              />
            </Button>
          </Dialogue.BtnGroup>
        </Dialogue>
      )}
    </Modal>
  );
};

interface ScopeOptionProps {
  checked: boolean;
  onChange: () => void;
  disabled: boolean;
  label: string;
  description: string;
  testId: string;
}

const ScopeOption = ({
  checked,
  onChange,
  disabled,
  label,
  description,
  testId
}: ScopeOptionProps) => (
  <label
    htmlFor={testId}
    className={`gap-x1-half rounded-x1 px-x2 py-x1-half flex cursor-pointer items-start border ${
      checked ? 'border-border-brand background-brand-subtle' : 'border-border-primary'
    }`}
  >
    <Radio
      id={testId}
      name='release-scope'
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className='mt-x0-quarter shrink-0'
      data-testid={testId}
    />
    <span className='flex flex-col'>
      <Text bold>{label}</Text>
      <Text size='s' color='secondary'>
        {description}
      </Text>
    </span>
  </label>
);

export default ReleaseGradesModal;
